import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BusinessIcon from "@mui/icons-material/Business";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment/moment";
import { useSelector, useDispatch } from "react-redux";
import { selectCustodians, selectAccountingData } from "../../Redux/Reducers/selectors";
import { findCust } from "../../component/Methods";


const FundsTable = ({
  funds,
  onAdd,
  onAddSMA,
  onEdit,
  onDelete,
  searchTerm,
  onSearchChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const custodians = useSelector(selectCustodians);
  const accountingData = useSelector(selectAccountingData);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddFund = () => {
    onAdd();
    handleMenuClose();
  };

  const handleAddSMA = () => {
    onAddSMA();
    handleMenuClose();
  };

  // Process funds data with accounting information like the original implementation
  const processedFunds = useMemo(() => {
    if (!funds || !accountingData) return [];

    const findAum = (key) => {
      if (key && accountingData && accountingData[0] && accountingData[0].funds) {
        const findName = accountingData[0]?.funds?.find((i) => {
          return i.fund == key;
        });
        return findName?.aum;
      } else {
        return 0;
      }
    };

    return funds?.map((row) => {
      const { fundId, accounts } = row;
      const SelectedAum = findAum(fundId);
      const fundAccounts = accounts
        ? Object.keys(accounts).map((key) => ({
            name: key,
            ...accounts[key],
          }))
        : [];

      const countCustodians = accounts ? Object.keys(accounts).length : 0;
      const countAccounts = accounts
        ? Object.values(accounts).reduce((acc, custodian) => {
            if (Array.isArray(custodian)) {
              return acc + custodian.length;
            }
            return acc + 1;
          }, 0)
        : 0;

      return {
        ...row,
        SelectedAum,
        countCustodians,
        countAccounts,
        fundAccounts,
      };
    }) || [];
  }, [funds, accountingData]);

  const columns = [
    {
      field: "abbreviation",
      headerName: "Abbreviation",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "isSMA",
      headerName: "Entity Type",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        const isSMA = params.value === true;
        return (
          <Chip
            label={isSMA ? "SMA" : "Fund"}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              color: isSMA
                ? theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0'
                : theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32',
              border: `1px solid ${isSMA 
                ? theme.palette.mode === 'dark' ? '#42a5f5' : '#bbdefb'
                : theme.palette.mode === 'dark' ? '#66bb6a' : '#c8e6c9'}`,
            }}
          />
        );
      },
    },
    {
      field: "name",
      headerName: "Full Name",
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "SelectedAum",
      headerName: "AUM",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        return params.value
          ? `$ ${params.value.toLocaleString("en-US", {
              style: "decimal",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}`
          : "$ 0";
      },
    },
    {
      field: "countCustodians",
      headerName: "No Of Custodians", 
      flex: 1,
      minWidth: 140,
    },
    {
      field: "countAccounts",
      headerName: "No Of Accounts",
      flex: 1,
      minWidth: 130,
    },
   
    {
      field: "date",
      headerName: "Creation Date",
      flex: 1,
      minWidth: 140,
      valueGetter: (value, row) => {
        return value ? moment(value).format("MMM DD, YYYY") : "-";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit(params.row)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.light + "20",
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">  
            <IconButton
              size="small"
              onClick={() => {
                onDelete(params.row);
              }}
              sx={{
                color: theme.palette.error.main,
                "&:hover": {
                  backgroundColor: theme.palette.error.light + "20",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    let data = processedFunds;
    
    // Apply search filter
    if (searchTerm) {
      data = data.filter((fund) =>
        fund.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fund.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort by abbreviation in ascending order (default behavior like original)
    data = [...data].sort((a, b) => {
      const aValue = a.abbreviation || '';
      const bValue = b.abbreviation || '';
      return aValue.localeCompare(bValue);
    });
    
    return data;
  }, [processedFunds, searchTerm]);

  // Add id field to each row for DataGrid and process account data
  const rowsWithId = useMemo(() => {
    return filteredData.map((fund) => ({
      ...fund,
      id: fund.fundId || fund.fund || Math.random().toString(),
      hasAccountData: (fund?.fundAccounts || [])?.length > 0,
    }));
  }, [filteredData]);

  const getDetailPanelContent = ({ row }) => {
    if (!row.hasAccountData) return null;

    return (
      <Box sx={{ p: 2, backgroundColor: theme.palette.grey[50] }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Fund Accounts
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, border: "none" }}>Account Names</TableCell>
              <TableCell sx={{ fontWeight: 600, border: "none" }}>Custodian</TableCell>
              <TableCell sx={{ fontWeight: 600, border: "none" }}>Number</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(row?.fundAccounts || []).map((account, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:hover": {
                    backgroundColor: theme.palette.grey[200],
                    cursor: "pointer",
                  },
                }}
              >
                <TableCell sx={{ borderRight: "none" }}>
                  {account?.name || "-"}
                </TableCell>
                <TableCell sx={{ borderLeft: "none", borderRight: "none" }}>
                  {findCust(account?.custodian, custodians) || "-"}
                </TableCell>
                <TableCell sx={{ borderLeft: "none" }}>
                  {account?.number || "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  const getDetailPanelHeight = ({ row }) => {
    if (!row.hasAccountData) return 0;
    // Base height + (number of accounts * row height) + padding
    return 120 + (row?.fundAccounts?.length || 0) * 40;
  };

  return (
    <Box sx={{width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search funds..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleMenuClick}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Add Entity
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleAddFund} sx={{ zoom: 0.8 }}>
              <ListItemIcon>
                <AccountBalanceWalletIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add Fund</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAddSMA} sx={{ zoom: 0.8 }}>
              <ListItemIcon>
                <BusinessIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add SMA</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <DataGridPro
        rows={rowsWithId}
        columns={columns}
        pageSize={1000}
        onPageSizeChange={(newPageSize) => onPageSizeChange(newPageSize)}
        rowsPerPageOptions={[25, 50, 100, 500, 1000, 2000]}
        pagination
        density="compact"
        disableSelectionOnClick
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        isDetailPanelToggleField={(field) => field === "hasAccountData"}
        sx={{
          border: "none",
          height: "calc(100vh - 20px)",
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-detailPanel": {
            backgroundColor: "transparent",
          },
        }}
      />
    </Box>
  );
};

export default FundsTable;